import { Button, Group, Modal, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React, { useState } from "react";

import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { uploadImage } from "api/image.service";
import { updateImageNames } from "api/product.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_DOWNLOAD_TITLE,
} from "common/constants";
import { ImageUploader } from "components/ImageUploader";
import { SafeImage } from "components/SafeImage ";
import { useProductStore } from "stores/useProductStore"; // adjust path as needed
import { ProductSummary } from "types/model.types";

interface UploadImagesModalProps {
  opened: boolean;
  onClose: () => void;
  product: ProductSummary;
  onSave?: (updated: Partial<ProductSummary>) => void;
}

const UploadImagesModal: React.FC<UploadImagesModalProps> = ({
  opened,
  onClose,
  product,
  onSave,
}) => {
  const [featuredImage, setFeaturedImage] = useState(product.featuredImageName);
  const [detailedImage, setDetailedImage] = useState(product.detailedImageName);
  const [isSavingImage1, setIsSavingImage1] = useState(false);
  const [isSavingImage2, setIsSavingImage2] = useState(false);
  const [uploadTrigger, setUploadTrigger] = useState<Date | null>(null);

  const handleUpload = async () => {
    setIsSavingImage1(true);
    setIsSavingImage2(true);
    setUploadTrigger(new Date()); // propagate the save click from parent down to child component to invoke the image upload function
  };

  const resolveProductName = (fn: string): string => {
    const extension = fn.substring(fn.lastIndexOf(".")); // ".jpg"
    return `${product.productCode}${extension}`;
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        setIsSavingImage1(false);
        setIsSavingImage2(false);
        onClose();
      }}
      title="Upload Product Images"
      size="lg"
      centered
    >
      <Stack>
        <Group grow align="flex-start">
          <Stack>
            <Text fw={600}>Featured Image</Text>
            <SafeImage
              key={"upload-image1"}
              src={`/images/${featuredImage}`}
              alt="Featured"
              width={40}
              height={80}
              style={{ objectFit: "contain" }}
              radius="md"
            />
            <ImageUploader
              key={"image-uploader1"}
              multiple={false}
              acceptedTypes={["image/jpeg", "image/png"]}
              comments="The recommended image size for this image is 75px x 183px."
              showUploadButton={false}
              uploadTrigger={uploadTrigger}
              onUpload={async (files: File[], paths?: string[]) => {
                if (!isSavingImage1) return;

                const file = files[0]; // only one file since multiple={false}
                const fullPath = paths?.[0]; // e.g. '/images/3.jpg'
                const overrideFilename = fullPath?.split("/").pop(); // returns '3.jpg'

                if (!file) {
                  notifications.show({
                    title: MSG_DEFAULT_DOWNLOAD_TITLE,
                    message: "No file selected for featured image to upload.",
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Warn,
                  });

                  return;
                }
                // You might extract 'isDetailed' from paths
                try {
                  const fn = resolveProductName(overrideFilename ?? "");
                  const response = await safeApiCall(
                    uploadImage(
                      file,
                      false, // feature image
                      fn,
                    ),
                  );
                  await safeApiCall(
                    updateImageNames(product.id, fn, product.detailedImageName),
                  );
                  notifications.show({
                    title: MSG_DEFAULT_DOWNLOAD_TITLE,
                    message: `Image uploaded successfully! ${response.data.location}`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Success,
                  });

                  useProductStore.setState((state) => ({
                    allProducts: {
                      ...state.allProducts,
                      items: state.allProducts.items.map((p) =>
                        p.id === product.id
                          ? { ...p, featuredImageName: fn }
                          : p,
                      ),
                    },
                  }));
                } catch (error) {
                  logError(error);
                } finally {
                  setIsSavingImage1(false);
                }
              }}
            />
          </Stack>

          <Stack>
            <Text fw={600}>Detailed Image</Text>

            <SafeImage
              key={"upload-image2"}
              src={`/images/d/${detailedImage}`}
              alt="Detailed"
              radius="md"
              width={40}
              style={{ objectFit: "contain" }}
              height={80}
            />
            <ImageUploader
              key={"image-uploader2"}
              multiple={false}
              acceptedTypes={["image/jpeg", "image/png"]}
              comments="The recommended image size for this image is 180px x 151px."
              showUploadButton={false}
              uploadTrigger={uploadTrigger}
              onUpload={async (files: File[], paths?: string[]) => {
                if (!isSavingImage2) return;

                const file = files[0]; // only one file since multiple={false}
                const fullPath = paths?.[0]; // e.g. '/images/3.jpg'
                const overrideFilename = fullPath?.split("/").pop(); // returns '3.jpg'

                if (isSavingImage2 && !file) {
                  notifications.show({
                    title: MSG_DEFAULT_DOWNLOAD_TITLE,
                    message: "No file selected for detailed image.",
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Warn,
                  });
                  console.error("No file selected for upload.");
                  return;
                }
                try {
                  const fn = resolveProductName(overrideFilename ?? "");
                  const response = await safeApiCall(
                    uploadImage(
                      file,
                      true, // detailed image
                      fn,
                    ),
                  );
                  await safeApiCall(
                    updateImageNames(product.id, product.featuredImageName, fn),
                  );
                  notifications.show({
                    title: MSG_DEFAULT_DOWNLOAD_TITLE,
                    message: `Image uploaded successfully! ${response.data.location}`,
                    position: MantineNotificationPosition.TopCenter,
                    color: MantineNotificationColor.Success,
                  });

                  useProductStore.setState((state) => ({
                    allProducts: {
                      ...state.allProducts,
                      items: state.allProducts.items.map((p) =>
                        p.id === product.id
                          ? { ...p, detailedImageName: fn }
                          : p,
                      ),
                    },
                  }));
                } catch (error) {
                  logError(error);
                } finally {
                  setIsSavingImage2(false);
                }
              }}
            />
          </Stack>
        </Group>

        <Group justify="flex-end" mt="md">
          <Button
            variant="default"
            onClick={() => {
              setIsSavingImage1(false);
              setIsSavingImage2(false);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            loading={isSavingImage1 || isSavingImage2}
            onClick={handleUpload}
          >
            Save Images
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default UploadImagesModal;
